/**
 * Quiet trust signals that sit right under the Hero.
 * Replaces the old MarqueeStrip + NumbersShowcase combo with a single
 * static row that doesn't compete for attention. Mobile: 2-col grid.
 * The Google rating tile links to the GMB review profile.
 */
import rating from '@/data/google-rating.json';
import { GOOGLE_REVIEWS_URL } from './GoogleRating';

const GMB_URL = 'https://g.page/r/CaXKbaQyBBmLEAE/review';
const hasRating = typeof rating.average === 'number' && typeof rating.total === 'number' && rating.total > 0;

// Each stat must be 100% defensible (FACTS.md, 2026-10-13): no counts, no
// ratings. "מאות מחשבים" and the five-star glyphs went in the copy pass —
// the GMB tile names the reviews without claiming a score; clicking shows
// the live truth.
const stats = [
  { value: 'מ-2018',   label: 'מעבדה בנטור' },                     // founded 2018
  { value: 'לכל הארץ', label: 'משלוח מחשבים עם UPS' },             // owner 2026-10-27 (FACTS: UPS nationwide)
  { value: 'בכתב',     label: 'מחיר לפני שמתחילים' },              // FACTS: price in writing (lab check is ₪150, not free)
  { value: '3 חודשים', label: 'אחריות על כל תיקון' },               // owner, 2026-10-07: repairs carry 3 months
  // 2026-10-20: the real Google rating, read from google-rating.json (synced daily
  // from the Business Profile API — FACTS.md allows exactly this, nothing typed by hand).
  hasRating
    ? { value: rating.average.toFixed(1), label: `${rating.total} ביקורות בגוגל`, href: GOOGLE_REVIEWS_URL }
    : { value: 'בגוגל', label: 'ביקורות של לקוחות', href: GMB_URL },
];

export default function TrustStrip() {
  return (
    <section className="font-heebo border-y border-[var(--cin-line)] bg-white py-7 md:py-9 px-[clamp(20px,5vw,80px)]">
      <div className="mx-auto max-w-[1280px]">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-x-4 gap-y-6 md:gap-x-6 items-start">
          {stats.map((s) => {
            const inner = (
              <>
                <div className="text-2xl md:text-3xl font-black tracking-tight text-[var(--cin-ink)]">
                  {s.value}
                </div>
                <div className="text-xs md:text-sm text-[var(--cin-muted)] mt-0.5">
                  {s.label}
                </div>
              </>
            );
            return s.href ? (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-center md:text-start group hover:opacity-80 transition-opacity"
                title="הביקורות שלנו בגוגל"
                aria-label="הביקורות שלנו בגוגל (נפתח בחלון חדש)"
              >
                {inner}
              </a>
            ) : (
              <div key={s.label} className="text-center md:text-start">
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
